import { Injectable, signal, computed } from '@angular/core';
import { Subject } from 'rxjs';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import type { ISablon, ISablonVeriKaynagi } from '@/src/service/fikoyt-ui/src/lib/paylasim-sistemi/sablon-editoru/sablon.interface';
import { VARSAYILAN_WIDGETLER } from './hazir-widgetler';

const kodWidgetler: ISablon[] = VARSAYILAN_WIDGETLER;

@Injectable({ providedIn: 'root' })
export class DashboardService {

  dbWidgetler = signal<ISablon[]>([]);
  tumWidgetler = computed(() => this.birlestir());
  private destroy$ = new Subject<void>();
  private soketAbonelikler = new Map<string, any>();

  async yukle(cihazId: string): Promise<void> {
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      istek.parametreler = {
        aggregate: [
          { $match: { vt: 'FikoYT.sistem.dashboard', sahip: cihazId, silindi: { $ne: true } } },
          { $limit: 1 },
        ],
      };
      await istek.isteSync();
      const sonuc = istek.cevap?.sonuc?.[0];
      this.dbWidgetler.set(sonuc?.veri?.widgetler || []);
    } catch {
      this.dbWidgetler.set([]);
    }
  }

  async kaydet(cihazId: string, widgetler: ISablon[]): Promise<void> {
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.guncelle' });
      istek.parametreler = {
        filtre: { vt: 'FikoYT.sistem.dashboard', sahip: cihazId },
        guncelleme: { $set: { vt: 'FikoYT.sistem.dashboard', sahip: cihazId, 'veri.widgetler': widgetler } },
        pipiSecenek: { upsert: true },
      };
      await istek.isteSync();
    } catch (e) {
      console.warn('Dashboard kaydet hatası:', e);
    }
  }

  private birlestir(): ISablon[] {
    const db = this.dbWidgetler();
    const dbIds = new Set(db.map(w => w.id));
    return [
      ...kodWidgetler.filter(kw => !dbIds.has(kw.id)).map(kw => ({ ...kw })),
      ...db,
    ]
      .filter(w => w.gorunur !== false && w.silindi !== true)
      .sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0));
  }

  async sablonVerisiCek(sablon: ISablon): Promise<any> {
    const vk = sablon.veriKaynagi;
    if (!vk) return null;
    try {
      switch (vk.mod) {
        case 'aggregate': {
          const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
          let pipeline = vk.pipeline;
          if (!pipeline && vk.vt) {
            pipeline = [{ $match: { vt: vk.vt } }];
          }
          if (!pipeline) return null;
          istek.parametreler = { aggregate: pipeline };
          await istek.isteSync();
          const sonuc = istek.cevap?.sonuc;
          if (vk.donusumFunc) {
            try { return new Function('veri', 'sablon', vk.donusumFunc)(sonuc, sablon); } catch (e) { console.warn('donusumFunc hatası:', e); }
          }
          return sonuc;
        }
        case 'sabit':
          return vk.sabitVeri ?? null;
        case 'soket':
          return null;
        case 'hesaplama': {
          if (!vk.piFunc) return null;
          try { return new Function('veri', 'sablon', vk.piFunc)(null, sablon); } catch (e) { console.warn('piFunc hatası:', e); }
          return null;
        }
        default:
          return null;
      }
    } catch {
      return null;
    }
  }

  sablonGuncelle(id: string, degisiklikler: Partial<ISablon>): void {
    this.dbWidgetler.update(liste => {
      const mevcut = liste.find(w => w.id === id);
      if (!mevcut) {
        const kodW = kodWidgetler.find(kw => kw.id === id);
        if (kodW) return [...liste, { ...kodW, ...degisiklikler, kaynak: 'db' as const }];
        return liste;
      }
      return liste.map(w => w.id === id ? { ...w, ...degisiklikler } : w);
    });
  }

  sablonEkle(sablon: ISablon): void {
    this.dbWidgetler.update(liste => [...liste, sablon]);
  }

  sablonSil(id: string): void {
    const kodWidget = kodWidgetler.find(kw => kw.id === id);
    this.dbWidgetler.update(liste => {
      const filtrelenmis = liste.filter(w => w.id !== id);
      if (kodWidget) {
        return [...filtrelenmis, { ...kodWidget, silindi: true, kaynak: 'db' as const }];
      }
      return filtrelenmis;
    });
  }

  layoutSifirla(): void {
    this.dbWidgetler.set([]);
  }

  soketDinle(sablon: ISablon, callback: (veri: any) => void): void {
    const vk = sablon.veriKaynagi;
    if (!vk?.soketOlay) return;

    const eventAdi = vk.soketOlay;
    const filtre = vk.soketFiltre;

    const handler = (payload: any) => {
      if (filtre) {
        const eslesiyor = Object.entries(filtre).every(([k, v]) => payload?.[k] === v);
        if (!eslesiyor) return;
      }
      callback(payload);
    };

    try {
      const mevcut = this.soketAbonelikler.get(sablon.id);
      if (mevcut) { mevcut.unsubscribe(); this.soketAbonelikler.delete(sablon.id); }

      const io = (window as any).FikoYT?.io;
      if (io?.on) {
        io.on(eventAdi, handler);
        this.soketAbonelikler.set(sablon.id, {
          unsubscribe: () => io.off(eventAdi, handler),
        });
      }
    } catch (e) {
      console.warn('Soket dinleme hatası:', sablon.id, e);
    }
  }

  soketAbonelikKaldir(widgetId: string): void {
    this.soketAbonelikler.get(widgetId)?.unsubscribe?.();
    this.soketAbonelikler.delete(widgetId);
  }

  personelDegisti(): void {
    this.soketAbonelikler.forEach(s => s?.unsubscribe?.());
    this.soketAbonelikler.clear();
  }

  temizle(): void {
    this.soketAbonelikler.forEach(s => s?.unsubscribe?.());
    this.soketAbonelikler.clear();
    this.dbWidgetler.set([]);
    this.destroy$.next();
  }
}
